import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import api from '../../api/config';
import { ROLES } from '../../api/endpoints';
import { extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { SweetAlert } from '../../utils/sweetAlert';
import { buildRoleListParams } from './roleApiHelpers';

const RoleList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [guardFilter, setGuardFilter] = useState('');
  const [guardInput, setGuardInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(ROLES.LIST, {
        params: buildRoleListParams({
          page: pagination.page,
          perPage: pagination.per_page,
          search,
          guardName: guardFilter,
        }),
      });
      const list = data?.data?.list ?? data?.list ?? (Array.isArray(data?.data) ? data.data : []);
      setRows(Array.isArray(list) ? list : []);
      const pag = extractPagination(data);
      setPagination((p) => ({ ...p, total: pag.total ?? list.length ?? 0 }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load roles');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search, guardFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
    setGuardFilter(guardInput.trim());
  };

  const handleClear = () => {
    setSearchInput('');
    setGuardInput('');
    setSearch('');
    setGuardFilter('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const goEdit = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/roles/edit/${id}`);
  };

  const handleDelete = async () => {
    const row = selectedRow;
    if (!row?.id) return;
    handleCloseMenu();

    const result = await SweetAlert.confirm({
      title: 'Delete role?',
      text: `Remove "${row.name}"? This action cannot be undone.`,
      confirmButtonText: 'Delete',
      icon: 'warning',
    });
    if (!result.isConfirmed) return;

    setActionLoadingId(row.id);
    try {
      await api.delete(ROLES.BY_ID(row.id));
      toast.success('Role deleted');
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete role');
    } finally {
      setActionLoadingId(null);
    }
  };

  const colCount = 5;

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AdminPanelSettingsIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Roles
            </Typography>
          </Box>
          <Can permissions={['role-create', 'role-view']}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/roles/create')}>
              Create role
            </Button>
          </Can>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search roles..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <TextField
            size="small"
            placeholder="Filter by guard name..."
            value={guardInput}
            onChange={(e) => setGuardInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 200 }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="text" onClick={handleClear}>
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Role name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Guard name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 72, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No roles found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{row.name ?? '—'}</TableCell>
                        <TableCell>
                          <Chip label={row.guard_name ?? '—'} size="small" variant="outlined" color="primary" />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.created_at ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            icon={row.deleted_at ? <LockIcon /> : <LockOpenIcon />}
                            label={row.deleted_at ? 'Inactive' : 'Active'}
                            color={row.deleted_at ? 'error' : 'success'}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            disabled={actionLoadingId === row.id || !!row.deleted_at}
                            aria-label="Actions"
                          >
                            {actionLoadingId === row.id ? <CircularProgress size={20} /> : <MoreVertIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Can permissions={['role-edit', 'role-view']}>
          <MenuItem onClick={goEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </Can>
        <Can permissions={['role-delete', 'role-view']}>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>
    </PageLayout>
  );
};

export default RoleList;
